import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { css } from 'styled-components'
import { DropdownButton } from 'components'
import { colors, _ } from 'utils'
import Icon from 'components/Icon'
import { SearchInput } from 'components/lists'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { SponsorType } from 'generated/graphql'
import DefaultLogo from 'assets/logo-placeholder.webp'
import { updateShow } from '../graphql/mutation'

const S = {
    ToggleButton: styled.div`
        display: flex;
        align-items: center;
        width: 130px;
        height: 32px;
        padding: 10px;
        font-size: 14px;
        color: ${colors.grayTitle};
        border-radius: 16px;
        background-color: white;
        border: 1px solid ${colors.iconOpacity(0.25)};
        user-select: none;
    `,
    SponsorSelect: styled.div`
        padding: 16px;
        min-width: 280px;
        background-color: white !important;
        & > .form-group > span {
            top: 12px;
        }
    `,
    SponsorItem: styled.div<{ active?: boolean }>`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 -16px;
        padding: 0 16px;
        width: calc(100% + 32px);
        height: 32px;
        line-height: 32px;
        font-size: 14px;
        color: ${({ active }) => (active ? 'white' : colors.grayTitle)};
        background-color: ${({ active }) => (active ? colors.primary : 'transparent')};
        ${({ active }) => !active && css`
            &:hover {
                color: ${colors.grayTitle};
                background-color: ${colors.pageBackground};
            }  
        `}
    `,
    SponsorLogo: styled.img`
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: contain;
        object-position: center;
    `
}

const ToggleButton = ({ sponsor }: { sponsor: SponsorType }) => (
    <S.ToggleButton>
        {sponsor ? <S.SponsorLogo src={sponsor?.logo || DefaultLogo} /> : <Icon icon="link" size={20} />}
        <div className="text-cut ml-1">
            {sponsor ? sponsor.name : _('show.form.link_sponsor')}
        </div>
    </S.ToggleButton>
)

interface Props {
    id: number
    value: SponsorType
    sponsors: SponsorType[]
    fetchData?: () => void
}

const SponsorField = ({
    id, value, sponsors = [], fetchData
}: Props) => {
    const dispatch = useDispatch()
    const [search, setSearch] = useState('')
    const searchSponsor = (sponsor: SponsorType) => (
        sponsor.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )

    const handleUpdateSponsor = async (_id: number, sponsorId: number | null) => {
        const response = await updateShow(+_id, { sponsorId })
        if (response.errors.length) {
            return dispatch(showErrorNotification(response.errors))
        }
        fetchData()
    }

    return (
        <DropdownButton
            renderToggleButton={() => <ToggleButton sponsor={value} />}
        >
            <S.SponsorSelect>
                <SearchInput
                    onChange={setSearch}
                    value={search}
                    debounce={100}
                    onClick={(e) => e.stopPropagation()}
                    className="mb-3"
                />
                {sponsors.filter(searchSponsor).map((sponsor) => {
                    const isActive = sponsor.id === value?.id
                    return (
                        <S.SponsorItem
                            key={sponsor.id}
                            active={isActive}
                            onClick={() => !isActive && handleUpdateSponsor(id, sponsor.id)}
                        >
                            {sponsor.name}
                            {isActive ? (
                                <Icon
                                    onClick={() => handleUpdateSponsor(id, null)}
                                    icon="close"
                                    color="white"
                                    style={{ marginBottom: 3 }}
                                />
                            ) : <div />}
                        </S.SponsorItem>
                    )
                })}
            </S.SponsorSelect>
        </DropdownButton>
    )
}

export default SponsorField
